import { createServerFn } from '@tanstack/react-start'
import { requireAuth, requireScopes, rateLimitFunction, createPostHogTrackingMiddleware } from '~/lib/middleware'
import { LogtoScopes } from '~/lib/logto/logto'
import { ApiResponse, Organization, organizationSchema } from '~/lib/schemas'
import { LogtoManagementApi } from '~/lib/logto/logto-management-api'
import type { LogtoOrganization } from '~/lib/logto/logto-management-api-types'
import z from 'zod'
import { createGroup } from '~/lib/abodemail/generated/api'
import { identifyOrganizationGroup } from '~/lib/posthog'

export const createOrganizationSchema = z.object({
  name: z.string().min(1, 'Organization name is required'),
  scopes: z.array(z.string()).optional(),
  evaluation_mode: z.boolean().default(false),
})

export type CreateOrganizationRequest = z.infer<typeof createOrganizationSchema>

export const createOrganization = createServerFn({
  method: 'POST',
})
  .middleware([
    requireAuth, 
    rateLimitFunction, 
    requireScopes(LogtoScopes.AdminOrgCrud),
    createPostHogTrackingMiddleware('create_organization')
  ])
  .validator(createOrganizationSchema)
  .handler(async (ctx) => {
    const { name, scopes, evaluation_mode } = ctx.data
    console.log('ctx.data', ctx.data)
      // Use real API
      const API_MANAGEMENT_BASE_URL = process.env.API_MANAGEMENT_BASE_URL || 'http://localhost:8000'
      const BFF_API_KEY = process.env.API_MANAGEMENT_API_KEY
      
      if (!BFF_API_KEY) {
        throw new Error('API_MANAGEMENT_API_KEY environment variable is required')
      }

      let logtoOrg: LogtoOrganization | null = null;
            //add to logto first if logto is setup 
      if (process.env.LOGTO_MANAGEMENT_ENDPOINT) {
        logtoOrg = await LogtoManagementApi.createOrganization({
          name,
          // description: 'Organization created by API',

          customData: {
            created_by: 'DaaS_Management_API',
          },
        })
      }
      
      
      const requestBody = {
        name,
        scopes,
        status: evaluation_mode ? "Evaluation" : "Active",
        logto_id: logtoOrg?.id,
      }


      


      try {
        const response = await fetch(`${API_MANAGEMENT_BASE_URL}/api/v4/admin/organizations`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${BFF_API_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }))
          throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`)
        }

        const result = await response.json() as ApiResponse<Organization>
        const newOrg = organizationSchema.parse(result.data)

        // Register the group up front so the org is named in PostHog from its
        // first event. Keyed by the Logto id where there is one, since that is
        // what org-scoped events attribute to.
        await identifyOrganizationGroup(logtoOrg?.id ?? newOrg.id, {
          name: newOrg.name,
        })

        // Create corresponding group in AbodeMail
        try {
          await createGroup({
            id: newOrg.id,
            name: newOrg.name,
          })
        
          console.log(`Created AbodeMail group for organization: ${newOrg.id}`)
        } catch (abodeMailError) {
          console.error('Failed to create AbodeMail group:', abodeMailError)
          // Don't fail the organization creation if AbodeMail group creation fails
        }

        return newOrg
      } catch (error) {
        // If organization creation failed, clean up Logto organization
        if (logtoOrg?.id) {
          try {
            await LogtoManagementApi.deleteOrganization(logtoOrg.id)
            console.log(`Cleaned up Logto organization: ${logtoOrg.id}`)
          } catch (cleanupError) {
            console.error('Failed to clean up Logto organization:', cleanupError)
          }
        }
        throw error
      }
  }) 
